import Button from "components/shared/button";
import { Popup } from "components/popups/Popup";
import React, { useEffect, useState } from "react";
import Input from "components/shared/inputs/input";
import useTranslation from "utils/hooks/useTranslation";
import useCustomForm from "utils/hooks/useCustomForm";
import { useCreateParameterMutation, useUpdateParameterMutation } from "store/api";
import { getStoredUser } from "utils/helpers/localStorage";
import type { DatasourceMutationRequest } from "./TvaPopup";

type EditableParameterRow = {
  id?: number | string;
  code?: string;
  value?: string;
};

type ParameterPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  request: DatasourceMutationRequest | null;
  envNumeric: number | undefined;
  mode?: "create" | "edit";
  row?: EditableParameterRow | null;
  onSaved?: () => Promise<void> | void;
};

const ParameterPopup: React.FC<ParameterPopupProps> = ({
  isOpen,
  onClose,
  request,
  envNumeric,
  mode = "edit",
  row,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [createParameter] = useCreateParameterMutation();
  const [updateParameter] = useUpdateParameterMutation();
  const isEditMode = mode === "edit";

  const userRole = getStoredUser<{ role?: string }>()?.role;
  const isSuperAdmin = userRole === "superadmin" || userRole === "hyperadmin";

  const { control, reset, getValues } = useCustomForm<{
    code: string;
    value: string;
  }>({
    defaultValues: {
      code: "",
      value: "",
    },
  });

  useEffect(() => {
    setIsSaving(false);
    reset({
      code: isOpen ? String(row?.code ?? "") : "",
      value: isOpen ? String(row?.value ?? "") : "",
    });
  }, [isOpen, reset, row?.code, row?.value]);

  const handleSave = async () => {
    if (!request) return;
    const rowId = row?.id ?? (row as any)?.key;
    if (isEditMode && rowId == null) return;
    if (!isEditMode && (envNumeric == null || Number.isNaN(envNumeric))) return;

    const { code, value } = getValues();
    const baseUrl = request.url.replace(/\/[^/]+$/, "");
    const url = isEditMode ? `${baseUrl}/${rowId}` : baseUrl;

    const payload: Record<string, unknown> = {
      value,
      env: envNumeric,
      ...(isSuperAdmin ? { code } : {}),
    };
    if (isEditMode) {
      payload.id = rowId;
    }

    setIsSaving(true);
    try {
      if (isEditMode) {
        await updateParameter({ url, data: payload }).unwrap();
      } else {
        await createParameter({ url, data: payload }).unwrap();
      }

      onSaved?.();
      onClose();
    } catch {
      // handled by api interceptor
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Popup
      title={isEditMode ? "Modifier paramètre" : "Nouveau paramètre"}
      onClose={onClose}
      portal
      fixSize="middle"
      closeOnOutsideClick
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={onClose} disabled={isSaving}>
              {t("Back")}
            </Button>
            <Button type="button" primary onClick={handleSave} disabled={isSaving}>
              {t(isEditMode ? "Update" : "Add")}
            </Button>
          </group>
        ),
      }}
    >
      <group
        data-direction="column"
        data-gap="20"
        data-space="40"
        data-space-horizontal="40"
        data-space-top="5"
        data-height="autofit"
      >
        <group data-gap="10" data-space-vertical="20">
          <group data-direction="column" data-gap="20">
            {isSuperAdmin && (
              <group data-gap="20">
                <group data-length="forcefit" data-fit="1">
                  <Input
                    dataLength="autofit"
                    label={t("Code")}
                    name="code"
                    control={control}
                    type="text"
                    size="large"
                    disabled={isEditMode}
                  />
                </group>
              </group>
            )}

            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={t("Value")}
                  name="value"
                  control={control}
                  type="textarea"
                  size="large"
                />
              </group>
            </group>
          </group>
        </group>
      </group>
    </Popup>
  );
};

export default ParameterPopup;
